import React, { useState } from "react";
import { usePlayer, useGame } from "@empirica/core/player/classic/react";

export function TaskResponse() {
  const player = usePlayer();
  const game = useGame();
  const [zoom, setZoom] = useState(100);

  // Load configuration
  const config = game.get("config");
  const marker = player.get("marker");
  const secondaryMarker = player.get("secondary_marker");

  // PDF URLs
  const pdfUrl = config?.stage1?.pdfUrl || "/pdfs/state.pdf";
  const aiPdfUrl = config?.stage1?.aiPdfUrl || pdfUrl; // fallback to main PDF

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 25, 200));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 25, 50));
  const handleZoomReset = () => setZoom(100);

  // Render PDF for Teacher with pdf or ai-pdf condition
  if (marker === 0 && (secondaryMarker === "pdf" || secondaryMarker === "ai-pdf")) {
    // Use AI-generated explanation PDF for ai-pdf condition, main PDF otherwise
    const displayPdfUrl = secondaryMarker === "ai-pdf" ? aiPdfUrl : pdfUrl;

    return (
      <div className="h-full flex flex-col">
        {/* Tip Banner - Focus on learning objectives in sidebar */}
        <div className="flex-shrink-0 bg-blue-50 border-b border-blue-200 px-4 py-3">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mt-0.5">
              <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-sm text-blue-800">
              <strong>Focus on the Learning Objectives</strong> in the left panel. Skim this material to understand those key concepts - you don't need to read everything.
            </p>
          </div>
        </div>

        {/* Zoom Controls */}
        <div className="flex-shrink-0 flex items-center justify-between px-3 py-2 bg-white border-b border-gray-200">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-gray-600">Zoom:</span>
            <div className="flex items-center bg-gray-100 rounded-lg shadow-sm">
              <button
                onClick={handleZoomOut}
                disabled={zoom <= 50}
                className="px-3 py-2 text-gray-700 hover:bg-gray-200 rounded-l-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                title="Zoom out"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                </svg>
              </button>
              <button
                onClick={handleZoomReset}
                className="px-4 py-2 text-sm font-bold text-gray-700 hover:bg-gray-200 transition-colors min-w-[70px] border-x border-gray-200"
                title="Reset zoom"
              >
                {zoom}%
              </button>
              <button
                onClick={handleZoomIn}
                disabled={zoom >= 200}
                className="px-3 py-2 text-gray-700 hover:bg-gray-200 rounded-r-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                title="Zoom in"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>
            </div>
          </div>
          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
            {secondaryMarker === "ai-pdf" ? "AI Explanation" : "Reference Material"}
          </span>
        </div>

        {/* PDF Viewer with Zoom */}
        <div className="flex-1 overflow-auto bg-gray-100 p-2">
          <div
            style={{
              transform: `scale(${zoom / 100})`,
              transformOrigin: 'top center',
              width: zoom > 100 ? `${10000 / zoom}%` : '100%',
              height: zoom > 100 ? `${10000 / zoom}%` : '100%',
            }}
          >
            <iframe
              src={`${displayPdfUrl}#view=FitH&pagemode=none`}
              title={secondaryMarker === "ai-pdf" ? "AI Explanation PDF" : "Reference Material PDF"}
              className="w-full h-full border border-gray-200 rounded-lg bg-white"
              style={{
                backgroundColor: 'white',
                filter: 'brightness(1.05) contrast(1.05)',
                minHeight: '100%',
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  return null;
}
